import { useState, useRef } from 'react';
import { Input } from './input';
import { cn } from '../../lib/utils';

/**
 * CurrencyInput - Numeric input with currency symbol prefix.
 * Fixes the "0187" issue by clearing zeros on focus and formatting on blur.
 */
export function CurrencyInput({
  value = 0,
  onValueChange,
  symbol = '$',
  className,
  ...props
}) {
  const [focused, setFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState('');
  const inputRef = useRef(null);

  const handleFocus = () => {
    setFocused(true);
    // Show raw number (empty if zero so user can type fresh)
    setDisplayValue(value === 0 || value === '' ? '' : String(value));
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseFloat(displayValue) || 0;
    onValueChange(parsed);
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    // Allow digits, decimal point, and empty string
    if (raw === '' || /^[0-9]*\.?[0-9]*$/.test(raw)) {
      setDisplayValue(raw);
      onValueChange(parseFloat(raw) || 0);
    }
  };

  // Formatted display when not focused
  const formattedValue = focused
    ? displayValue
    : value === 0
    ? ''
    : value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <div className="relative">
      <span className={cn(
        "absolute left-2 top-1/2 -translate-y-1/2 text-xs font-medium pointer-events-none",
        value > 0 || focused ? "text-muted-foreground" : "text-muted-foreground/50"
      )}>
        {symbol}
      </span>
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={formattedValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder="0.00"
        className={cn("pl-5", className)}
        {...props}
      />
    </div>
  );
}

export default CurrencyInput;
